import { Theme } from '@mui/material/styles'

import { icons, layout } from '../tokens.generated'
//
import {
  CheckboxCheckedIcon,
  CheckboxIcon,
  CheckboxIndeterminateIcon,
} from './CustomIcons'

// ----------------------------------------------------------------------
// A tick box is a control on the dense step, and its word sits one
// `controlGap` from the square an operator can see.
//
// The padding here used to be a whole spacing step. With the glyph's own 3px
// margin on the 24 grid and the row's 10px gap on top of it, the distance from
// the painted square to the first letter came to 21px — 2.6 times the 8 the
// system publishes for a glyph and its label — and it was paid twice
// vertically as well, so two stacked rows carried 16px of dead space before
// their parent's spacing was counted at all. No single file held the total,
// which is why nobody could see it was wrong.
//
// So the hit box becomes `controlHeightSmall`, the console's dense control
// step, and the inset this file owns is whatever is left once the glyph is
// drawn inside it. `layout.tickBoxInset` is the sum — that padding plus the
// glyph's own margin — and `ControlLabel` subtracts it so the gap the operator
// measures is the gap the system published.
// ----------------------------------------------------------------------

/**
 * What the control pads around its glyph. Derived from the two boxes rather
 * than typed, so the hit box is provably the dense control step.
 */
export const TICK_PADDING = (layout.controlHeightSmall - icons.sizes.action) / 2

export default function Checkbox(theme: Theme) {
  return {
    MuiCheckbox: {
      defaultProps: {
        icon: <CheckboxIcon />,
        checkedIcon: <CheckboxCheckedIcon />,
        indeterminateIcon: <CheckboxIndeterminateIcon />,
      },

      styleOverrides: {
        root: {
          'padding': TICK_PADDING,
          // A box that cannot be pressed still has to be read. MUI's
          // `action.disabled` is `alpha(greyText, 0.8)`, which composites to
          // #B5B9BE on a white card: 1.98:1 against the 3:1 a control's
          // boundary owes. Dark composites to ~3.4:1 and passed on its own,
          // which is why only the light sweep found it.
          //
          // `controlEdge` is the role for exactly this — the ink of a control
          // that is drawn but not selected — and it is measured on all three
          // grounds in both schemes. One selector, not two: a disabled tick
          // takes the same neutral as a disabled empty box, because the glyph
          // is the whole control. The shape says which way it is set and the
          // ink says it is not live; keeping the accent on the tick would say
          // "live" with colour and "not live" with nothing.
          '&.Mui-disabled': {
            color: theme.palette.controlEdge,
          },
          '& .MuiSvgIcon-fontSizeMedium': {
            width: 24,
            height: 24,
          },
          '& .MuiSvgIcon-fontSizeSmall': {
            width: 20,
            height: 20,
          },
          'svg': {
            'fontSize': 24,
            '&[font-size=small]': {
              fontSize: 20,
            },
          },
        },
      },
    },
  }
}
